import { apiClient } from "../api/ApiClient";
import type { PagedResult } from "../models/PagedResult";
import type { SearchParam } from "../models/SearchParam";
import type { DBModel } from "../models/DBModel";

export interface CrudServiceOptions<TPartial> {
  Route: string;
  SearchReferences?: boolean;
  PaginationInPath?: boolean;
  WarehouseCode?: string;
  WarehouseFields?: string[];
  BaseFilters?: SearchParam["Filters"];
  EntityName: string;
  PartialFields: string[];
  LoadingMessage: string;
  SavingMessage: string;
  MapPartial?: (value: Record<string, unknown>) => TPartial;
  DeleteRequest?: (code: string) => Promise<unknown>;
  DeleteAction?: string;
  SaveRequest?: (data: unknown[]) => Promise<unknown[]>;
}

export interface CrudService<T extends DBModel, TPartial = T> {
  Save: (data: T[]) => Promise<T[]>;
  Delete?: (code: string) => Promise<unknown>;
  GetPartial: (skip?: number, limit?: number) => Promise<PagedResult<TPartial>>;
  GetAll: (skip?: number, limit?: number) => Promise<PagedResult<T>>;
  SearchByAnyParam: (
    param: string,
    skip?: number,
    limit?: number,
  ) => Promise<PagedResult<T>>;
  Search: (
    skip: number,
    limit: number,
    search: SearchParam,
  ) => Promise<PagedResult<T>>;
}

export type ICRUDService<T extends DBModel, TPartial = T> = CrudService<
  T,
  TPartial
>;

export function CreateCrudService<T extends DBModel, TPartial = T>(
  options: CrudServiceOptions<TPartial>,
): CrudService<T, TPartial> {
  const scope = (search: SearchParam): SearchParam =>
    options.WarehouseCode
      ? ({
          ...search,
          Filters: [
            ...(search.Filters ?? []),
            ...(options.WarehouseFields ?? ["WareHouse"]).map((Field) => ({
              Field,
              Operator: "EQ",
              Value: options.WarehouseCode,
            })),
          ],
          Condition: (options.WarehouseFields?.length ?? 1) > 1 ? "Or" : "And",
        } as SearchParam)
      : ({
          ...search,
          Filters: [...(search.Filters ?? []), ...(options.BaseFilters ?? [])],
        } as SearchParam);
  const Save = async (data: T[]): Promise<T[]> => {
    data = data.map(row => ({...row, Code: row.Code ?? ""}));
    if (options.SaveRequest) return (await options.SaveRequest(data)) as T[];
    const response = await apiClient.post<T[]>(
      `${options.Route}/Save${options.EntityName}`,
      data,
      {
        busyMessage: options.SavingMessage,
      },
    );
    return response.data;
  };

  const GetPartial = async (
    skip = 0,
    limit = 50,
  ): Promise<PagedResult<TPartial>> => {
    const response = await apiClient.post<PagedResult<Record<string, unknown>>>(
      `${options.Route}/SearchDynamic/${skip}/${limit}`,
      scope({ Fields: options.PartialFields } as SearchParam),
      {
        busyMessage: options.LoadingMessage,
        params: options.WarehouseCode
          ? { warehouse: options.WarehouseCode }
          : undefined,
      },
    );
    return {
      ...response.data,
      Data: options.MapPartial
        ? response.data.Data.map(options.MapPartial)
        : (response.data.Data as TPartial[]),
    };
  };

  const GetAll = async (skip = 0, limit = 50): Promise<PagedResult<T>> => {
    if (options.WarehouseCode || options.BaseFilters?.length)
      return Search(skip, limit, {} as SearchParam);
    const response = await apiClient.get<PagedResult<T>>(
      options.PaginationInPath
        ? `${options.Route}/GetAll/${skip}/${limit}`
        : `${options.Route}/GetAll?skip=${skip}&limit=${limit}`,
      {
        busyMessage: options.LoadingMessage,
        params: options.WarehouseCode
          ? { warehouse: options.WarehouseCode }
          : undefined,
      },
    );
    return response.data;
  };

  const SearchByAnyParam = async (
    param: string,
    skip = 0,
    limit = 50,
  ): Promise<PagedResult<T>> => {
    const response = await apiClient.post<PagedResult<T>>(
      options.PaginationInPath
        ? `${options.Route}/Search/${skip}/${limit}`
        : `${options.Route}/Search?skip=${skip}&limit=${limit}`,
      scope({ Search: param, SearchReferences: options.SearchReferences ?? true } as SearchParam),
      {
        busyMessage: options.LoadingMessage,
        params: options.WarehouseCode
          ? { warehouse: options.WarehouseCode }
          : undefined,
      },
    );
    return response.data;
  };

  const Search = async (
    skip = 0,
    limit = 50,
    search: SearchParam,
  ): Promise<PagedResult<T>> => {
    const response = await apiClient.post<PagedResult<T>>(
      options.PaginationInPath
        ? `${options.Route}/Search/${skip}/${limit}`
        : `${options.Route}/Search?skip=${skip}&limit=${limit}`,
      scope(search),
      {
        busyMessage: options.LoadingMessage,
        params: options.WarehouseCode
          ? { warehouse: options.WarehouseCode }
          : undefined,
      },
    );
    return response.data;
  };

  const Delete =
    options.DeleteRequest ??
    (options.DeleteAction
      ? async (code: string) =>
          (
            await apiClient.post(
              `${options.Route}/${options.DeleteAction}`,
              null,
              { params: { code }, busyMessage: "Deleting record..." },
            )
          ).data
      : undefined);
  return { Save, GetPartial, GetAll, SearchByAnyParam, Search, Delete };
}

export function PageService<T extends DBModel, TPartial = T>(
  options: CrudServiceOptions<TPartial>,
): ICRUDService<T, TPartial> {
  return CreateCrudService<T, TPartial>(options);
}
